import { useEffect, useRef } from 'react';
import { useConfigStore } from '../store/useConfigStore';

// RichAds Telegram SDK types
interface TelegramAdsControllerInstance {
  initialize: (config: { pubId: string; appId: string }) => void;
  showPush: () => Promise<boolean>;
  showInterstitialBanner: () => Promise<boolean>;
  showInterstitialVideo: () => Promise<boolean>;
  showEmbeddedBanner: (containerId: string) => Promise<boolean>;
}

declare global {
  interface Window {
    TelegramAdsController?: new () => TelegramAdsControllerInstance;
    richAdsInstance?: TelegramAdsControllerInstance;
  }
}

let isInitialized = false;

/**
 * Initialize RichAds SDK with config from store
 */
export function initRichAds(): boolean {
  if (isInitialized && window.richAdsInstance) return true;
  
  const config = useConfigStore.getState();
  
  if (!config.richads.enabled) {
    console.log('RichAds is disabled');
    return false;
  }
  
  if (!window.TelegramAdsController) {
    console.warn('RichAds SDK not loaded');
    return false;
  }
  
  try {
    const pubId = config.richads.pubId || '833686';
    const appId = config.richads.appId || '5025';
    
    window.richAdsInstance = new window.TelegramAdsController();
    window.richAdsInstance.initialize({ pubId, appId });
    isInitialized = true;
    console.log('RichAds initialized with pubId:', pubId, 'appId:', appId);
    return true;
  } catch (error) {
    console.error('Failed to initialize RichAds:', error);
    return false;
  }
}

/**
 * Show RichAds Push-style ad
 */
export async function showRichAdsPush(): Promise<boolean> {
  const config = useConfigStore.getState();
  
  if (!config.adsEnabled || !config.richads.enabled) {
    console.warn('RichAds push ads are disabled');
    return false;
  }
  
  if (!initRichAds() || !window.richAdsInstance) {
    return false;
  }
  
  try {
    const result = await window.richAdsInstance.showPush();
    console.log('RichAds push ad result:', result);
    return result;
  } catch (error) {
    console.error('RichAds push ad error:', error);
    return false;
  }
}

/**
 * Show RichAds Interstitial Banner
 */
export async function showRichAdsInterstitialBanner(): Promise<boolean> {
  const config = useConfigStore.getState();
  
  if (!config.adsEnabled || !config.richads.enabled) {
    console.warn('RichAds interstitial banner ads are disabled');
    return false;
  }
  
  if (!initRichAds() || !window.richAdsInstance) {
    return false;
  }
  
  try {
    const result = await window.richAdsInstance.showInterstitialBanner();
    console.log('RichAds interstitial banner result:', result);
    return result;
  } catch (error) {
    console.error('RichAds interstitial banner error:', error);
    return false;
  }
}

/**
 * Show RichAds Interstitial Video
 */
export async function showRichAdsInterstitialVideo(): Promise<boolean> {
  const config = useConfigStore.getState();
  
  if (!config.adsEnabled || !config.richads.enabled) {
    console.warn('RichAds video ads are disabled');
    return false;
  }
  
  if (!initRichAds() || !window.richAdsInstance) {
    return false;
  }
  
  try {
    const result = await window.richAdsInstance.showInterstitialVideo();
    console.log('RichAds interstitial video result:', result);
    return result;
  } catch (error) {
    console.error('RichAds interstitial video error:', error);
    return false;
  }
}

/**
 * Show RichAds Embedded Banner
 */
export async function showRichAdsEmbeddedBanner(containerId: string): Promise<boolean> {
  const config = useConfigStore.getState();
  
  if (!config.adsEnabled || !config.richads.enabled) {
    console.warn('RichAds banner ads are disabled');
    return false;
  }
  
  if (!initRichAds() || !window.richAdsInstance) {
    return false;
  }
  
  try {
    const result = await window.richAdsInstance.showEmbeddedBanner(containerId);
    console.log('RichAds embedded banner result:', result);
    return result;
  } catch (error) {
    console.error('RichAds embedded banner error:', error);
    return false;
  }
}

/**
 * Hook to use RichAds in components
 */
export function useRichAds() {
  const richadsConfig = useConfigStore((state) => state.richads);
  const isInitializedRef = useRef(false);
  
  useEffect(() => {
    if (richadsConfig.enabled && !isInitializedRef.current) {
      // Small delay to ensure SDK script is loaded
      const timer = setTimeout(() => {
        if (initRichAds()) {
          isInitializedRef.current = true;
        }
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [richadsConfig.enabled, richadsConfig.pubId, richadsConfig.appId]);
  
  return {
    isEnabled: richadsConfig.enabled,
    showPush: showRichAdsPush,
    showInterstitialBanner: showRichAdsInterstitialBanner,
    showInterstitialVideo: showRichAdsInterstitialVideo,
    showEmbeddedBanner: showRichAdsEmbeddedBanner,
  };
}
